/**
 * AppointmentRow — the shared Today-list row for appointment, monitoring, and
 * task entries (#3191). Home and Care both render Today rows through
 * `TodayList`; medication rows already share `MedicationRow`, and this is the
 * equivalent primitive for every non-medication row: time chip, leading art,
 * title/detail with completion strike-through, optional completion checkbox,
 * and the partner ownership tag.
 */

import { Image, Pressable, Text, View } from "react-native";
import { Check } from "lucide-react-native";
import {
  PrimaryTitleText,
  SupportingSubtitleText,
  cardSurface,
} from "@louise/moon-ui";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { OrganicMiniIcon } from "./OrganicMiniIcon";
import { PartnerAvatarTag } from "./PartnerAvatarTag";
import type { TodayRow } from "./HomepageReviewConceptHome";

const appointmentRowIllustration =
  require("@src/apps/moon/assets/images/stethoscope-copper.png") as number;

export function getTodayRowIllustration(row: TodayRow) {
  const title = row.title.toLowerCase();
  if (
    row.kind === "appointment" ||
    row.status === "appointment" ||
    row.status === "scheduled" ||
    title.includes("appointment") ||
    title.includes("visit") ||
    title.includes("draw") ||
    title.includes("labs")
  ) {
    return appointmentRowIllustration;
  }
  return null;
}

export function isCompletableTodayRow(row: TodayRow): boolean {
  // Medication rows complete through the dose-log mutation, not the
  // persisted Today-action note used by appointments and tasks.
  if (row.kind === "medication") return false;
  if (row.completable !== undefined) return row.completable;
  return (
    row.kind === "appointment" ||
    (row.kind === "monitoring" && Boolean(row.appointmentId))
  );
}

export function AppointmentRow({
  row,
  isComplete,
  showCheckbox,
  appearance = "default",
  onPress,
  onCheckboxPress,
}: {
  row: TodayRow;
  isComplete: boolean;
  /** Caller decides completability (it owns the completion dialog state). */
  showCheckbox: boolean;
  appearance?: "default" | "calendar-glass";
  onPress: () => void;
  onCheckboxPress: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("AppointmentRow");
  const rowIllustration = getTodayRowIllustration(row);
  const isPartnerOwned = Boolean(row.partnerName);
  const isCalendarGlass = appearance === "calendar-glass";

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => {
        capturePress(pressLabel(row.title), `today_row_${row.kind}`);
        onPress();
      }}
      style={({ pressed }) => ({
        ...(isCalendarGlass
          ? cardSurface({ variant: "panel", tone: "onDark" })
          : cardSurface()),
        flexDirection: "row",
        alignItems: "center",
        gap: isCalendarGlass ? 10 : 12,
        paddingHorizontal: isCalendarGlass ? 11 : 16,
        paddingVertical: isCalendarGlass ? 10 : 13,
        opacity: pressed ? 0.86 : isComplete ? 0.72 : 1,
        transform: [{ scale: pressed ? 0.995 : 1 }],
      })}
    >
      {row.time ? (
        <View style={{ width: 50, flexShrink: 0 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              lineHeight: 15,
              color: isComplete
                ? isCalendarGlass
                  ? semanticColors.onDark.text.tertiary
                  : semanticColors.text.faint
                : isCalendarGlass
                  ? semanticColors.onDark.text.tertiary
                  : EMBER.colors.textLight,
              fontWeight: "800",
              textDecorationLine: isComplete ? "line-through" : "none",
              textDecorationColor: semanticColors.text.faint,
            }}
          >
            {row.time}
          </Text>
        </View>
      ) : null}
      {!isCalendarGlass ? (
        <View
          style={{
            width: 34,
            height: 34,
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {rowIllustration ? (
            <Image
              source={rowIllustration}
              resizeMode="contain"
              style={{ width: 32, height: 32 }}
            />
          ) : (
            <OrganicMiniIcon kind={row.icon} size={30} />
          )}
        </View>
      ) : null}
      <View style={{ flex: 1 }}>
        {isCalendarGlass ? (
          <>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12.5,
                lineHeight: 16,
                color: isComplete
                  ? semanticColors.onDark.text.tertiary
                  : semanticColors.onDark.text.primary,
                fontWeight: "900",
                textDecorationLine: isComplete ? "line-through" : "none",
              }}
            >
              {row.title}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11.5,
                lineHeight: 15,
                color: isComplete
                  ? semanticColors.onDark.text.tertiary
                  : semanticColors.onDark.text.secondary,
                marginTop: 1,
                textDecorationLine: isComplete ? "line-through" : "none",
              }}
            >
              {row.detail}
            </Text>
          </>
        ) : (
          <>
            <PrimaryTitleText
              style={{
                color: isComplete ? semanticColors.text.tertiary : undefined,
                textDecorationLine: isComplete ? "line-through" : "none",
                textDecorationColor: semanticColors.text.tertiary,
              }}
            >
              {row.title}
            </PrimaryTitleText>
            <SupportingSubtitleText
              style={{
                color: isComplete ? semanticColors.text.faint : undefined,
                textDecorationLine: isComplete ? "line-through" : "none",
                textDecorationColor: semanticColors.text.faint,
              }}
            >
              {row.detail}
            </SupportingSubtitleText>
          </>
        )}
      </View>
      {showCheckbox && isCompletableTodayRow(row) ? (
        <Pressable
          accessibilityRole="checkbox"
          accessibilityLabel={
            isComplete
              ? t("homepage.review_concept.completed_a11y", {
                  params: { label: row.title },
                })
              : t("homepage.review_concept.complete_for_a11y", {
                  params: { label: row.title },
                })
          }
          accessibilityState={{ checked: isComplete }}
          disabled={isComplete}
          onPress={(event) => {
            event.stopPropagation?.();
            capturePress(
              pressLabel(row.title),
              `today_row_${row.kind}_checkbox`,
            );
            onCheckboxPress();
          }}
          hitSlop={8}
          style={{
            width: 30,
            height: 30,
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              backgroundColor: isComplete
                ? semanticColors.status.success
                : "transparent",
              borderWidth: isComplete ? 0 : 1.5,
              borderColor: isComplete
                ? semanticColors.status.success
                : isCalendarGlass
                  ? semanticColors.onDark.text.secondary
                  : semanticColors.border.strong,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isComplete ? (
              <Check
                size={13}
                color={semanticColors.text.inverse}
                strokeWidth={3}
              />
            ) : null}
          </View>
        </Pressable>
      ) : null}
      {isPartnerOwned && row.partnerName ? (
        <View
          style={{
            width: 74,
            alignItems: "flex-end",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <PartnerAvatarTag label={row.partnerName} />
        </View>
      ) : null}
    </Pressable>
  );
}
